import { useLocale } from 'next-intl';
import { PropsWithChildren, createContext, memo, use, useCallback, useMemo, useState } from 'react';

import { useGetCurrentUrl } from '../hooks/useGetCurrentUrl';
import { useRouter } from '../routing/routing';
import { Language } from '../types/schemas';

type Languages = {
	availableLanguages?: Language[];
};

type LocalizationContextProps = {
	languages: Languages;
	setLocale: (localeCode: string) => void;
	setLanguages: (languages: Languages) => void;
	locale: string;
};

const LocalizationContext = createContext<LocalizationContextProps>({
	languages: {},
	setLocale: () => {},
	setLanguages: () => {},
	locale: 'en',
});

export const useLocalization = () => use(LocalizationContext);

export const LocalizationProvider = memo(({ children }: PropsWithChildren) => {
	const router = useRouter();
	const locale = useLocale();
	const getCurrentUrl = useGetCurrentUrl();

	const [languages, setLanguages] = useState<Languages>({});

	const onLocaleChange = useCallback(
		(locale: string) => {
			router.push(getCurrentUrl(), { locale });
		},
		[router, getCurrentUrl],
	);

	const value = useMemo<LocalizationContextProps>(
		() => ({
			languages,
			setLocale: onLocaleChange,
			setLanguages,
			locale,
		}),
		[locale, languages, onLocaleChange],
	);

	return <LocalizationContext value={value}>{children}</LocalizationContext>;
});

LocalizationProvider.displayName = 'LocalizationProvider';
